import { Action, Feedback, Role, VoiceChatMessage } from "@/lib/messaging";
import { useCallback, useEffect, useRef, useState } from "react";

import { AudioChatMessageComponent } from "@/components/pages/voicechat/audio-chat-message";
import { FeedbackAnalysisCard } from "@/components/pages/voicechat/feedback-analysis-card";
import { ThinkingMessageComponent } from "@/components/pages/voicechat/thinking-message";

interface AudioChatProps {
  messages?: VoiceChatMessage[];
  isAutoPlay?: boolean;
  setAudioPlaying: (isPlaying: boolean) => void;
  restartMode: () => void;
}

export default function BaseChatComponent({
  messages,
  isAutoPlay = true,
  setAudioPlaying,
  restartMode,
}: AudioChatProps) {
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const [audioPlayingChildren, setAudioPlayingChildren] = useState<string[]>(
    [],
  );
  const [feedback, setFeedback] = useState<Feedback | undefined>(undefined);
  const [continuationType, setContinuationType] = useState("continue");
  const [isVoicechatThinking, setVoicechatThinking] = useState<boolean>(false);

  const handleActiveChange = useCallback(
    (childId: string, isActive: boolean) => {
      setAudioPlayingChildren((prev) => {
        if (isActive && !prev.includes(childId)) {
          return [...prev, childId];
        } else if (!isActive && prev.includes(childId)) {
          return prev.filter((id) => id !== childId);
        }
        return prev;
      });
    },
    [],
  );

  useEffect(() => {
    if (audioPlayingChildren.length > 0) {
      setAudioPlaying(true);
    } else {
      setAudioPlaying(false);
    }
  }, [audioPlayingChildren]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    console.log("messages: ", messages);
    scrollToBottom();

    if (!messages || messages.length == 0) {
      // if mode was reset, messages cleared, set thinking state to false
      setVoicechatThinking(false);
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.action == Action.Thinking) {
      setVoicechatThinking(true);
    } else if (
      lastMessage.role == Role.Assistant &&
      lastMessage.action == Action.Message
    ) {
      setVoicechatThinking(false);
    }

    // Set feedback if the last message is an analysis
    if (lastMessage.action == Action.Analysis && lastMessage.feedback) {
      const feedback = lastMessage.feedback;
      setFeedback(feedback);
    }

    // Set feedback type
    if (["argumentbattle", "improtest"].includes(lastMessage.mode)) {
      // Game ends after analysis feedback
      setContinuationType("end");
    } else {
      setContinuationType("continue");
    }
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [feedback]);

  return (
    <div className="pb-4 md:pb-0">
      <div className="flex flex-col space-y-4">
        {messages?.map((message, idx) =>
          message.message &&
          (message.role == Role.User || message.role == Role.Assistant) ? (
            <div
              key={idx}
              className={`flex ${message.role == Role.User ? "justify-end ml-8" : "justify-start mr-8"}`}
            >
              <AudioChatMessageComponent
                id={String(idx)}
                src={message.audioSrc}
                text={message.message || ""}
                play={
                  isAutoPlay &&
                  message.role == Role.Assistant &&
                  idx == messages.length - 1
                    ? true
                    : false
                }
                onAudioActiveChange={handleActiveChange}
                feedback={message.hasFeedback ? message.feedback : undefined}
              />
            </div>
          ) : message.role == Role.System &&
            message.action == Action.Analysis &&
            feedback ? (
            <FeedbackAnalysisCard
              key={idx}
              feedbackData={feedback}
              restartMode={restartMode}
              continuationType={continuationType as "continue" | "end"}
            />
          ) : null,
        )}
        {isVoicechatThinking && <ThinkingMessageComponent />}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
